import Dexie, { type EntityTable } from 'dexie';
import type { Job } from '@/types/job';
import type { Inspection } from '@/types/inspection';
import type { Report } from '@/types/report';
import type { SyncQueueItem, MediaQueueItem } from '@/types/sync';

/**
 * StructureAI local IndexedDB database using Dexie.js.
 *
 * This is the offline-first data layer. All writes go here first,
 * then the sync engine pushes changes to the server when online.
 *
 * Tables:
 * - jobs: Cached job documents
 * - inspections: Cached inspection documents
 * - syncQueue: Pending mutations (POST, PUT, DELETE) awaiting server sync
 * - mediaQueue: Binary blobs (photos, audio) awaiting upload
 * - cachedReports: Downloaded report HTML/metadata for offline viewing
 */
interface DatabaseScope {
  tenantId: string;
  userId: string;
}

class StructureAIDatabase extends Dexie {
  jobs!: EntityTable<Job, 'id'>;
  inspections!: EntityTable<Inspection, 'id'>;
  syncQueue!: EntityTable<SyncQueueItem, 'id'>;
  mediaQueue!: EntityTable<MediaQueueItem, 'id'>;
  cachedReports!: EntityTable<Report, 'id'>;

  constructor(name: string) {
    super(name);

    this.version(1).stores({
      jobs: 'id, jobReference, status, createdBy, updatedAt, clientName',
      inspections: 'id, jobId, elementType, sequenceNumber, createdAt',
      syncQueue: '++id, status, entityType, entityId, createdAt',
      mediaQueue: '++id, status, entityType, jobId, inspectionId, createdAt',
      cachedReports: 'id, jobId, status, updatedAt',
    });
  }
}

const ANONYMOUS_SCOPE_KEY = 'anonymous';

function getScopeKey(scope: DatabaseScope | null): string {
  if (!scope?.tenantId || !scope.userId) {
    return ANONYMOUS_SCOPE_KEY;
  }
  return `${scope.userId}:${scope.tenantId}`;
}

function getDatabaseName(scope: DatabaseScope | null): string {
  const scopeKey = getScopeKey(scope).replace(/[^a-zA-Z0-9:_-]/g, '_');
  return `StructureAI__${scopeKey}`;
}

let currentScopeKey = ANONYMOUS_SCOPE_KEY;
let currentDb = new StructureAIDatabase(getDatabaseName(null));

function getActiveDatabase(): StructureAIDatabase {
  return currentDb;
}

/** Scope the local database to the authenticated user and tenant context. */
export function configureLocalDatabase(scope: DatabaseScope | null): void {
  const nextScopeKey = getScopeKey(scope);
  const nextName = getDatabaseName(scope);

  if (currentDb.name === nextName) {
    currentScopeKey = nextScopeKey;
    return;
  }

  currentDb.close();
  currentDb = new StructureAIDatabase(nextName);
  currentScopeKey = nextScopeKey;
}

/** Current scope key used by the active local database instance. */
export function getCurrentDatabaseScopeKey(): string {
  return currentScopeKey;
}

/** Proxy that always forwards operations to the active scoped database. */
export const db = new Proxy({} as StructureAIDatabase, {
  get(_target, prop, receiver) {
    const value = Reflect.get(getActiveDatabase(), prop, receiver);
    return typeof value === 'function' ? value.bind(getActiveDatabase()) : value;
  },
}) as StructureAIDatabase;

/**
 * Clear all local data. Used on logout.
 */
export async function clearLocalDatabase(): Promise<void> {
  const activeDb = getActiveDatabase();
  await activeDb.transaction('rw', [activeDb.jobs, activeDb.inspections, activeDb.syncQueue, activeDb.mediaQueue, activeDb.cachedReports], async () => {
    await activeDb.jobs.clear();
    await activeDb.inspections.clear();
    await activeDb.syncQueue.clear();
    await activeDb.mediaQueue.clear();
    await activeDb.cachedReports.clear();
  });
}
